import {useAdminApi} from 'quickstart/hooks/useApi'
import {useMemo} from 'react'
import {AdminMetaObject, Paged} from 'tizra'
import * as A from '../admin'
import {ImageBlockConfig} from './meta'

export const Admin: A.Admin<ImageBlockConfig> = ({values}) => {
  // This little TypeScript dance because useAdminApi has the same typing as
  // useApi. It doesn't know that it's getting enhanced results.
  const {data: _data} = useAdminApi.list({metaType: ['Image']})
  const data = _data as Paged<AdminMetaObject> | undefined

  const {images, imageOptions} = useMemo(() => {
    const images = Object.fromEntries(
      data?.result
        .filter(img => img.publishedStaging)
        .map(img => [img.tizraId, img]) || [],
    )
    const imageOptions = [
      // First item will be "custom URL" according to noneOption prop.
      {label: 'cover image', value: '_cover'},
      {label: '---', value: '---'}, // TODO: switch to A.Dropdown
      ...A.makeOptions<AdminMetaObject>({
        labelKey: x => `${x.name} (${x.tizraId})`,
        valueKey: 'tizraId',
      })(Object.values(images)),
    ]
    return {images, imageOptions}
  }, [data])

  const chosenImage = images[values.tizraId] as AdminMetaObject | undefined
  const hint =
    !chosenImage ? undefined
    : !chosenImage.publishedLive ?
      'Warning: This image is not published to your live site!'
    : chosenImage.hasUnpublishedChanges ?
      'Warning: This image has unpublished changes.'
    : undefined

  return (
    <A.Tabs>
      <A.Tab title="Content">
        <A.Select
          label="Image"
          name="tizraId"
          noneOption="custom URL"
          options={imageOptions}
          hint={hint}
        />
        <A.Input
          label="Custom image URL"
          name="imageUrl"
          long
          hidden={!!values.tizraId}
        />
        <A.Input
          label="Meta-object tizra-id for cover image"
          name="coverId"
          hint="Leave blank for current object."
          hidden={values.tizraId !== '_cover'}
        />
        <A.Input
          label="Alt text"
          name="altText"
          long
          hidden={!!values.tizraId}
        />
        <A.Input
          label="Link to"
          name="linkUrl"
          hint="Destination if image is clicked."
          long
        />
      </A.Tab>
      <A.Tab title="Presentation">
        <A.SubGroup title="Bleed">
          <A.Radio
            name="width"
            options={A.options({
              bleed: 'Full-bleed to browser edge',
              contain: 'Fit image in container',
            })}
          />
        </A.SubGroup>
        <A.SubGroup title="Surround">
          <A.Radio
            name="surround"
            options={A.options({
              none: 'No padding or shadow (for banners)',
              padding: 'Padding above and below',
              shadow: 'Padding and drop shadow',
            })}
          />
        </A.SubGroup>
        <A.SubGroup title="Constraints">
          <A.Input label="Max width" name="maxWidth" />
          <A.Input label="Max height" name="maxHeight" />
        </A.SubGroup>
        <A.SubGroup title="Scaling">
          <A.Radio
            name="fitting"
            options={A.options({
              contain: (
                <span>
                  Contain &mdash; The image will be scaled up or down, then
                  pillarboxed as needed to preserve aspect ratio.
                </span>
              ),
              'scale-down': (
                <span>
                  Scale-down &mdash; Same as contain, but the image is not
                  allowed to scale larger than its natural pixel dimensions.
                </span>
              ),
              cover: (
                <span>
                  Cover &mdash; The image will be scaled up or down, then
                  cropped as needed to preserve aspect ratio.
                </span>
              ),
            })}
          ></A.Radio>
        </A.SubGroup>
        <A.SubGroup title="Position">
          <A.JustificationInput
            label="If the image doesn't fill the available space, where should the image be placed?"
            name="position"
          />
        </A.SubGroup>
        <A.SubGroup title="Focus">
          <A.CompassInput
            label="If the image overflows the available space, which part of the image should be retained?"
            name="focus"
          />
        </A.SubGroup>
      </A.Tab>
    </A.Tabs>
  )
}
